import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ServicesList, EditServiceModal } from '../components/dashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyServices = ({ user, onLogout, onUserUpdate }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  
  // Edit service modal state
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingServiceImages, setEditingServiceImages] = useState([]);
  const [savingService, setSavingService] = useState(false);
  const [uploadingEditImages, setUploadingEditImages] = useState(false);

  const effectiveRole = user?.active_role || user?.role;

  // Redirect if not a provider
  useEffect(() => {
    if (effectiveRole !== 'provider') {
      navigate('/dashboard', { replace: true });
    }
  }, [effectiveRole, navigate]);

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, []);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/services/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const openEditServiceModal = (service) => {
    setEditingService(service);
    setEditingServiceImages(service.images || []);
    setShowEditServiceModal(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      alert(error.response?.data?.detail || 'Failed to delete service');
    }
  };

  const handleToggleServiceStatus = async (service) => {
    const newStatus = service.status === 'paused' ? 'active' : 'paused';
    // Optimistic update
    setServices((prev) => prev.map((s) => (s.id === service.id ? { ...s, status: newStatus } : s)));
    try {
      await axios.put(`${API}/services/${service.id}/status`, { status: newStatus });
    } catch (error) {
      console.error('Error updating service status:', error);
      alert(error.response?.data?.detail || 'Failed to update status');
      fetchServices(); // revert on failure
    }
  };

  const handleEditServiceImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (editingServiceImages.length + files.length > 10) {
      alert('Maximum 10 images allowed');
      return;
    }

    setUploadingEditImages(true);

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Each file must be less than 5MB');
        continue;
      }

      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        alert('Only JPEG, PNG, and WebP images are allowed');
        continue;
      }

      try {
        const uploadData = new FormData();
        uploadData.append('file', file);

        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/upload/service-image`, uploadData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data && response.data.url) {
          setEditingServiceImages(prev => [...prev, response.data.url]);
          setEditingService(prev => ({
            ...prev,
            images: [...(prev.images || []), response.data.url]
          }));
        }
      } catch (error) {
        console.error('Image upload error:', error);
        alert(`Failed to upload image: ${error.response?.data?.detail || error.message}`);
      }
    }

    setUploadingEditImages(false);
  };

  const handleRemoveEditServiceImage = (index) => {
    setEditingServiceImages(prev => prev.filter((_, i) => i !== index));
    setEditingService(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }));
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    if (!editingService) return;
    
    setSavingService(true);
    try {
      await axios.put(`${API}/services/${editingService.id}`, {
        title: editingService.title,
        description: editingService.description,
        category: editingService.category,
        price: parseFloat(editingService.price),
        price_unit: editingService.price_unit,
        currency: editingService.currency || 'PHP',
        location: editingService.location,
        images: editingServiceImages,
        skills_required: editingService.skills_required || []
      });
      setShowEditServiceModal(false);
      setEditingService(null);
      setEditingServiceImages([]);
      fetchServices();
      alert('Service updated successfully!');
    } catch (error) {
      console.error('Error updating service:', error);
      alert(error.response?.data?.detail || 'Failed to update service');
    } finally {
      setSavingService(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-600">{t('ux.loadingServices')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="my-services-page">
      <Navbar user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="my-services-title">
              {t('ux.myServices')}
            </h1>
            <p className="text-gray-600 mt-1">
              {t('ux.manageListings')}
            </p>
          </div>
          <Link
            to="/post-service"
            className="bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition flex items-center gap-2"
            data-testid="post-new-service-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('ux.postNewService')}
          </Link>
        </div>

        {/* Services List */}
        {services.length > 0 ? (
          <ServicesList
            services={services}
            onEdit={openEditServiceModal}
            onDelete={handleDeleteService}
            onToggleStatus={handleToggleServiceStatus}
          />
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('ux.noServicesYet')}</h3>
            <p className="text-gray-500 mb-6">{t('ux.noServicesDesc')}</p>
            <Link
              to="/post-service"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('ux.createFirstService')}
            </Link>
          </div>
        )}
      </div>

      {/* Edit Service Modal */}
      {showEditServiceModal && (
        <EditServiceModal
          service={editingService}
          onServiceChange={setEditingService}
          images={editingServiceImages}
          onImageUpload={handleEditServiceImageUpload}
          onImageRemove={handleRemoveEditServiceImage}
          onSave={handleUpdateService}
          onClose={() => {
            setShowEditServiceModal(false);
            setEditingService(null);
            setEditingServiceImages([]);
          }}
          saving={savingService}
          uploading={uploadingEditImages}
          categories={categories}
          user={user}
        />
      )}

      <Footer />
    </div>
  );
};

export default MyServices;
